import { useState } from "react";
import {
  Chip,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { ServiceTicket, ServiceTicketDetails } from "../types";

interface ServiceTicketDetailsDialogProps {
  open: boolean;
  ticket: ServiceTicket | null;
  details: ServiceTicketDetails | null;
  loading?: boolean;
  onClose: () => void;
}

const money = (value: unknown) => `Rs ${Number(value || 0).toLocaleString()}`;

const Detail = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={800}>
      {label.toUpperCase()}
    </Typography>
    <Typography fontWeight={700}>{value ?? "-"}</Typography>
  </Stack>
);

const ServiceTicketDetailsDialog = ({
  open,
  ticket,
  details,
  loading = false,
  onClose,
}: ServiceTicketDetailsDialogProps) => {
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  if (!ticket) return null;

  const spareIssues = details?.spareIssues || [];
  const spareIssueItems = spareIssues.flatMap((issue) =>
    (issue.items || []).map((item) => ({
      issue,
      item,
      partName: item.sparePart?.name || item.sparePartId,
      partCode: item.sparePart?.code || item.sparePart?.partNumber || "-",
      quantity: Number(item.quantity || 0),
      unitCost: Number(item.unitCost || 0),
    }))
  );
  const totalIssueAmount = spareIssueItems.reduce(
    (sum, row) => sum + row.quantity * row.unitCost,
    0
  );
  const spareReturnItems = (details?.spareReturns || []).flatMap((spareReturn) =>
    (spareReturn.items || []).map((item) => ({
      spareReturn,
      item,
      partName: item.sparePart?.name || item.sparePartId,
      partCode: item.sparePart?.code || item.sparePart?.partNumber || "-",
    }))
  );
  const spareUsageItems = (details?.spareUsages || []).map((usage) => ({
    ...usage,
    partName: usage.sparePart?.name || usage.sparePartId,
    partCode: usage.sparePart?.code || usage.sparePart?.partNumber || "-",
    quantityUsed: Number(usage.quantityUsed || 0),
    unitCost: Number(usage.unitCost || 0),
  }));
  const invoice = details?.serviceInvoice || ticket.serviceInvoice;
  const consumedSpareCost =
    invoice?.spareCharge !== undefined
      ? Number(invoice.spareCharge || 0)
      : spareUsageItems.reduce(
          (sum, row) => sum + row.quantityUsed * row.unitCost,
          0
        );
  const repairCost =
    invoice?.labourCharge !== undefined
      ? Number(invoice.labourCharge || 0)
      : Number(ticket.actualRepairCost || details?.actualRepairCost || 0);
  const totalMaintenanceCost =
    invoice?.totalAmount !== undefined
      ? Number(invoice.totalAmount || 0)
      : consumedSpareCost + repairCost;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6" fontWeight={900}>
              Ticket Details: {ticket.ticketNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {ticket.vehicleNumber} - {ticket.serviceType}
            </Typography>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Detail label="Vehicle" value={ticket.vehicleNumber} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Detail label="Chassis" value={ticket.chassisNumber} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Detail label="Technician" value={ticket.assignedTechnicianName} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary" fontWeight={800}>
                  STATUS
                </Typography>
                <Chip
                  size="small"
                  color={ticket.status === "CLOSED" ? "success" : "primary"}
                  label={ticket.status}
                  sx={{ width: "fit-content", fontWeight: 800 }}
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12} md={3}>
              <Detail label="Priority" value={ticket.priority} />
            </Grid>
            <Grid item xs={12} md={3}>
              <Detail
                label="Created"
                value={new Date(ticket.createdAt).toLocaleString()}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Detail
                label="Closed"
                value={
                  ticket.closedAt ? new Date(ticket.closedAt).toLocaleString() : "-"
                }
              />
            </Grid>
            {ticket.serviceType === "ON_ROAD" && (
              <Grid item xs={12} md={3}>
                <Detail
                  label={ticket.status === "EXPIRED" ? "Expired At" : "Expires At"}
                  value={
                    ticket.expiredAt
                      ? new Date(ticket.expiredAt).toLocaleString()
                      : ticket.expiresAt
                        ? new Date(ticket.expiresAt).toLocaleString()
                        : "-"
                  }
                />
              </Grid>
            )}
            {ticket.serviceType === "ON_ROAD" && (
              <>
                <Grid item xs={12} md={3}>
                  <Detail
                    label="Client Name"
                    value={details?.clientName ?? ticket.clientName}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Detail
                    label="Client Rider ID"
                    value={details?.clientRiderID ?? ticket.clientRiderID}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} md={3}>
              <Detail label="Spare Issues" value={spareIssues.length ? "Yes" : "No"} />
            </Grid>

            <Grid item xs={12}>
              <Detail label="Issue Title" value={ticket.issueTitle} />
            </Grid>
            <Grid item xs={12}>
              <Detail label="Issue Description" value={ticket.issueDescription} />
            </Grid>
            {ticket.serviceType === "ON_ROAD" && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    maxWidth: 520,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={900}
                    sx={{ minWidth: 150 }}
                  >
                    Service Ticket Image
                  </Typography>
                  {details?.imageUrl || ticket.imageUrl ? (
                    <>
                      <ButtonBase
                        onClick={() => setImageViewerOpen(true)}
                        aria-label="View service ticket image"
                        sx={{
                          position: "relative",
                          width: 120,
                          height: 84,
                          flexShrink: 0,
                          overflow: "hidden",
                          borderRadius: 1,
                          bgcolor: "grey.100",
                          border: "1px solid",
                          borderColor: "divider",
                          "&:hover .image-overlay": { opacity: 1 },
                        }}
                      >
                        <Box
                          component="img"
                          src={details?.imageUrl || ticket.imageUrl || undefined}
                          alt={`Service ticket image for ${ticket.ticketNumber}`}
                          sx={{
                            display: "block",
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                        <Box
                          className="image-overlay"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "common.white",
                            bgcolor: "rgba(0,0,0,0.48)",
                            opacity: 0,
                            transition: "opacity 180ms ease",
                          }}
                        >
                          <ZoomInIcon fontSize="small" />
                        </Box>
                      </ButtonBase>
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700}>
                          Attached image
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ticket.ticketNumber}
                        </Typography>
                        <Typography
                          component="button"
                          type="button"
                          onClick={() => setImageViewerOpen(true)}
                          sx={{
                            p: 0,
                            border: 0,
                            bgcolor: "transparent",
                            color: "primary.main",
                            font: "inherit",
                            fontSize: 12,
                            fontWeight: 800,
                            textAlign: "left",
                            cursor: "pointer",
                          }}
                        >
                          View image
                        </Typography>
                      </Stack>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No image uploaded
                    </Typography>
                  )}
                </Box>
                <Dialog
                  open={imageViewerOpen}
                  onClose={() => setImageViewerOpen(false)}
                  maxWidth="xl"
                  fullWidth
                >
                  <DialogTitle sx={{ pr: 7 }}>
                    Service Ticket Image: {ticket.ticketNumber}
                    <IconButton
                      onClick={() => setImageViewerOpen(false)}
                      aria-label="Close image viewer"
                      sx={{
                        position: "absolute",
                        right: 12,
                        top: 10,
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </DialogTitle>
                  <DialogContent sx={{ bgcolor: "#101820", p: { xs: 1, md: 2 } }}>
                    <Box
                      component="img"
                      src={details?.imageUrl || ticket.imageUrl || undefined}
                      alt={`Full-size inspection photo for ticket ${ticket.ticketNumber}`}
                      sx={{
                        display: "block",
                        width: "100%",
                        maxHeight: "75vh",
                        objectFit: "contain",
                      }}
                    />
                    <ButtonBase
                      component="a"
                      href={details?.imageUrl || ticket.imageUrl || undefined}
                      target="_blank"
                      rel="noreferrer"
                      sx={{ mt: 1, color: "common.white", gap: 0.5, px: 1, py: 0.5, borderRadius: 1 }}
                    >
                      <OpenInNewIcon fontSize="small" />
                      <Typography variant="caption" fontWeight={800}>
                        Open signed image URL
                      </Typography>
                    </ButtonBase>
                  </DialogContent>
                </Dialog>
              </Grid>
            )}
            {(ticket.status === "CLOSED" || invoice) && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Detail label="Consumed Spare Cost" value={money(consumedSpareCost)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Detail label="Actual Repair Cost" value={money(repairCost)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Detail label="Total Maintenance Cost" value={money(totalMaintenanceCost)} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Detail label="Invoice" value={invoice?.code || "-"} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Detail label="Resolution" value={ticket.resolution || details?.resolution || "-"} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Detail
                    label="Closure Remarks"
                    value={ticket.closureRemarks || details?.closureRemarks || "-"}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={900}>
              Consumed Spare Usage
            </Typography>
            {loading ? (
              <Typography color="text.secondary">Loading spare usage...</Typography>
            ) : spareUsageItems.length ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Usage Code</TableCell>
                      <TableCell>Spare</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell align="right">Used Qty</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="right">Consumed Cost</TableCell>
                      <TableCell>Used At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {spareUsageItems.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.code}</TableCell>
                        <TableCell>{row.partName}</TableCell>
                        <TableCell>{row.partCode}</TableCell>
                        <TableCell align="right">{row.quantityUsed}</TableCell>
                        <TableCell align="right">{money(row.unitCost)}</TableCell>
                        <TableCell align="right">
                          {money(row.quantityUsed * row.unitCost)}
                        </TableCell>
                        <TableCell>{new Date(row.usedAt).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5}>
                        <strong>Total Consumed Spare Cost</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{money(consumedSpareCost)}</strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No consumed spare usage recorded for this ticket yet.
              </Typography>
            )}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={900}>
              Spare Issues
            </Typography>
            {loading ? (
              <Typography color="text.secondary">Loading ticket details...</Typography>
            ) : spareIssueItems.length ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Issue Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Spare</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Unit Cost</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Issued At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {spareIssueItems.map((row) => (
                      <TableRow key={`${row.issue.id}-${row.item.id}`}>
                        <TableCell>{row.issue.code}</TableCell>
                        <TableCell>{row.issue.status}</TableCell>
                        <TableCell>{row.partName}</TableCell>
                        <TableCell>{row.partCode}</TableCell>
                        <TableCell align="right">{row.quantity}</TableCell>
                        <TableCell align="right">{money(row.unitCost)}</TableCell>
                        <TableCell align="right">
                          {money(row.quantity * row.unitCost)}
                        </TableCell>
                        <TableCell>
                          {new Date(row.issue.issuedAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={6}>
                        <strong>Total Issued Cost</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{money(totalIssueAmount)}</strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No spare parts have been issued for this ticket yet.
              </Typography>
            )}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={900}>
              Spare Returns
            </Typography>
            {loading ? (
              <Typography color="text.secondary">Loading return details...</Typography>
            ) : spareReturnItems.length ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Return Code</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Spare</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell align="right">Issued</TableCell>
                      <TableCell align="right">Used</TableCell>
                      <TableCell align="right">Returned</TableCell>
                      <TableCell align="right">Accepted</TableCell>
                      <TableCell align="right">Damaged</TableCell>
                      <TableCell>Verified</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {spareReturnItems.map((row) => (
                      <TableRow key={`${row.spareReturn.id}-${row.item.id}`}>
                        <TableCell>{row.spareReturn.returnCode}</TableCell>
                        <TableCell>{row.spareReturn.status}</TableCell>
                        <TableCell>{row.partName}</TableCell>
                        <TableCell>{row.partCode}</TableCell>
                        <TableCell align="right">{row.item.issuedQty}</TableCell>
                        <TableCell align="right">{row.item.usedQty}</TableCell>
                        <TableCell align="right">{row.item.returnedQty}</TableCell>
                        <TableCell align="right">{row.item.acceptedQty}</TableCell>
                        <TableCell align="right">{row.item.damagedQty}</TableCell>
                        <TableCell>
                          {row.spareReturn.verifiedAt
                            ? new Date(row.spareReturn.verifiedAt).toLocaleString()
                            : "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No spare returns have been submitted for this ticket yet.
              </Typography>
            )}
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={900}>
              Assignments
            </Typography>
            {details?.assignments?.length ? (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Technician</TableCell>
                      <TableCell>Assigned By</TableCell>
                      <TableCell>Assigned At</TableCell>
                      <TableCell>Remarks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.assignments.map((assignment) => (
                      <TableRow key={assignment.id}>
                        <TableCell>{assignment.technicianId}</TableCell>
                        <TableCell>{assignment.assignedById}</TableCell>
                        <TableCell>
                          {new Date(assignment.assignedAt).toLocaleString()}
                        </TableCell>
                        <TableCell>{assignment.remarks || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No technician assignment history available.
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceTicketDetailsDialog;
